import {patchState, signalStore, withComputed, withHooks, withLinkedState, withMethods, withProps, withState} from "@ngrx/signals";
import {computed, DestroyRef, inject, linkedSignal, signal} from "@angular/core";
import {ExamFlow, ExamFlowScheduleMode, ExamScheduleEntry, ExamScheduleSettings, ExamSession, ExamStudentToStationMapping} from "@models";
import {GeneratedTypePredicates, SimExamFlowService, SimSessionService} from "@services";
import {effectOnceIf} from "ngxtension/effect-once-if";
import {ActivatedRoute} from "@angular/router";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {EventEmitterService} from "@event-emitter";

export type ExamSchedulePageStore = {
	examFlow: ExamFlow | null;
	session: ExamSession | null;
	hasError: boolean;
};

export const ExamSchedulePageStore = signalStore(
	withState<ExamSchedulePageStore>(() => ({
		examFlow: null,
		session: null,
		hasError: false,
	})),
	withProps(() => ({
		sessionService: inject(SimSessionService),
		examFlowService: inject(SimExamFlowService),
		eventEmitter: inject(EventEmitterService),
		route: inject(ActivatedRoute),
		destroyRef: inject(DestroyRef),
	})),
	withLinkedState((store) => ({
		scheduleModel: linkedSignal<ExamScheduleEntry[]>(() => store.session()?.schedule ?? []),
	})),
	withComputed((store) => ({
		runIds: computed<string[]>(() => store.session()?.schedule.map((x) => x.id) ?? []),
		isLoading: computed<boolean>(() => !store.session() || !store.examFlow()),
		isReadonly: computed<boolean>(() => store.session()?.isEditable === false),
		scheduleMode: computed<ExamFlowScheduleMode>(
			() => store.session()?.scheduleSettings.scheduleMode ?? ExamFlowScheduleMode.AutoSchedule,
		),
	})),
	withMethods((store) => ({
		async scheduleUpdated(request: {
			runId: string;
			to: ExamStudentToStationMapping;
			from: ExamStudentToStationMapping;
		}): Promise<ExamScheduleEntry[]> {
			const newSchedule = await store.sessionService.switchStudentBetweenStationsInRun({
				examSessionId: store.session()!.id,
				examScheduleEntryId: request.runId,
				from: request.from,
				to: request.to,
			});
			patchState(store, () => ({scheduleModel: newSchedule}));
			return newSchedule;
		},
		async scheduleSettingsUpdated(settings: ExamScheduleSettings) {
			const session = await store.examFlowService.updateExamScheduleSettings(store.session()!.id, settings);
			patchState(store, () => ({session}));
		},
	})),
	withHooks((store) => ({
		onInit() {
			const sessionId = signal<string>("");

			// Get sessionId from URL
			store.route.paramMap
				.pipe(takeUntilDestroyed(store.destroyRef))
				.subscribe(async (param) => sessionId.set(param.get("sessionId") ?? ""));

			store.eventEmitter.onTrainingSessionUpdated.subscribe(({simSession}) => {
				if (simSession.id === store.session()?.id && GeneratedTypePredicates.isExamSession(simSession)) {
					patchState(store, () => ({session: simSession}));
				}
			});

			// Fetch examFlow
			effectOnceIf(
				() => store.session()?.examFlowId,
				async () => {
					const examFlow = await store.examFlowService.getExamFlowById(store.session()!.examFlowId);
					if (examFlow) {
						patchState(store, () => ({examFlow}));
					} else {
						patchState(store, () => ({hasError: true}));
					}
				},
			);

			// Fetch session
			effectOnceIf(
				() => sessionId(),
				async () => {
					const session = await store.sessionService.getSessionById(sessionId());
					if (session && GeneratedTypePredicates.isExamSession(session)) {
						patchState(store, () => ({session}));
					} else {
						patchState(store, () => ({hasError: true}));
					}
				},
			);
		},
	})),
);
